import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function polyLatex(a2, a1, a0) {
  const t2 = a2 === 1 ? "x^2" : a2 === -1 ? "-x^2" : `${a2}x^2`;
  const t1 =
    a1 === 0
      ? ""
      : a1 === 1
        ? "+x"
        : a1 === -1
          ? "-x"
          : a1 > 0
            ? `+${a1}x`
            : `${a1}x`;
  const t0 = a0 === 0 ? "" : a0 > 0 ? `+${a0}` : `${a0}`;
  return `${t2}${t1}${t0}`;
}

function getUniqueDistractors(correct, candidates) {
  const out = [];
  const used = new Set([correct]);
  for (const c of candidates) {
    if (used.has(c)) continue;
    used.add(c);
    out.push(c);
    if (out.length === 3) break;
  }
  return out;
}

function signedConstLatex(value) {
  return value >= 0 ? `+${value}` : `-${Math.abs(value)}`;
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaBinomialDifferencePolynomialClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 4,
    },
    data: {
      a: 3,
      b: 2,
      c: 2,
      d: 3,
      s1: 1,
      s2: -1,
      exprLatex: "(3x+2)^2-(2x-3)^2",
      coeffX2: 5,
      coeffX: 24,
      constant: -5,
      options: [
        { label: "A", latex: "5x^2-5", isCorrect: false },
        { label: "B", latex: "5x^2+13", isCorrect: false },
        { label: "C", latex: "5x^2+24x-5", isCorrect: true },
        { label: "D", latex: "5x^2+24x-13", isCorrect: false },
      ],
      correctIndex: 2,
      maxPoints: 1,
    },
  };
}

export function generateTask(generateOptions = {}) {
  if (
    generateOptions.variant === "original" &&
    generateOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  // Najczesciej zachowujemy konstrukcje oryginalu CKE:
  // (px +/- q)^2 - (qx -/+ p)^2. Dzieki zamianie p i q mamy ab = cd,
  // wiec dystraktory odpowiadaja dokladnie typowym bledom rachunkowym.
  // Szerszy wariant pozostaje, aby zadanie nie bylo wyuczalne z jednego schematu.
  const coupledVariant = Math.random() < 0.7;

  let a;
  let c;
  let b;
  let d;

  if (coupledVariant) {
    // np. (3x+2)^2 - (2x-3)^2
    a = pick([2, 3, 4, 5, 6, 7]);
    b = pick(Array.from({ length: a - 1 }, (_, index) => index + 1));
    c = b;
    d = a;
  } else {
    // wariant losowy, ale zawsze a > c
    a = pick([4, 5, 6, 7, 8]);
    c = pick([2, 3, 4, 5, 6, 7]);
    while (c >= a) c = pick([2, 3, 4, 5, 6, 7]);
    b = pick([1, 2, 3, 4, 5]);
    d = pick([1, 2, 3, 4, 5]);
  }

  // Zamiana "sumy z roznica" wewnatrz nawiasow:
  // false -> (ax + b)^2 - (cx - d)^2
  // true  -> (ax - b)^2 - (cx + d)^2
  const swappedSigns = Math.random() < 0.5;
  const s1 = swappedSigns ? -1 : 1; // znak przy b w pierwszym nawiasie
  const s2 = swappedSigns ? 1 : -1; // znak przy d w drugim nawiasie

  const coeffX2 = a * a - c * c;
  const coeffX = 2 * a * s1 * b - 2 * c * s2 * d;
  const constant = b * b - d * d;

  const correctLatex = polyLatex(coeffX2, coeffX, constant);
  const sumConst = b * b + d * d;

  // Dystraktory sa wynikami konkretnych, typowych bledow.
  // 1. Pominiecie obu wyrazow 2ab.
  const wrongNoLinearCorrectConst = polyLatex(coeffX2, 0, constant);
  // 2. Minus przed drugim nawiasem zmienia tylko pierwszy jego skladnik.
  //    Dla rodziny oryginalu wyrazy liniowe wtedy sie redukuja.
  const wrongOuterMinusCoeffX = 2 * a * s1 * b + 2 * c * s2 * d;
  const wrongOuterMinus = polyLatex(coeffX2, wrongOuterMinusCoeffX, sumConst);
  // 3. Odjecie obu skladnikow stalych zamiast obliczenia b^2-d^2.
  const wrongLinearMinusSumConst = polyLatex(coeffX2, coeffX, -sumConst);

  // Rezerwy chronia szerokie warianty przed zduplikowaniem odpowiedzi.
  const wrongOppositeLinear = polyLatex(coeffX2, -coeffX, constant);
  const wrongAddedSquares = polyLatex(a * a + c * c, coeffX, constant);

  const distractors = getUniqueDistractors(correctLatex, [
    wrongNoLinearCorrectConst,
    wrongOuterMinus,
    wrongLinearMinusSumConst,
    wrongOppositeLinear,
    wrongAddedSquares,
  ]);

  const optionsRaw = [
    { latex: correctLatex, isCorrect: true },
    ...distractors.map((latex) => ({ latex, isCorrect: false })),
  ];

  const options = shuffle(optionsRaw).map((o, idx) => ({
    label: LETTERS[idx],
    latex: o.latex,
    isCorrect: o.isCorrect,
  }));
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaBinomialDifferencePolynomialClosedTask",
    source: { variant: "random", basedOn: "MATURA_2025_MAJ_4" },
    data: {
      a,
      b,
      c,
      d,
      s1,
      s2,
      exprLatex: `(${a}x${signedConstLatex(s1 * b)})^2-(${c}x${signedConstLatex(s2 * d)})^2`,
      coeffX2,
      coeffX,
      constant,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { exprLatex, options } = data;
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>{"Zadanie maturalne - zamkni\u0119te."}</strong>
      </p>
      <p>{"Doko\u0144cz zdanie. Wybierz w\u0142a\u015bciw\u0105 odpowied\u017a spo\u015br\u00f3d podanych."}</p>
      <p>
        {"Dla ka\u017cdej liczby rzeczywistej "}
        <InlineMath math={"x"} />
        {" warto\u015b\u0107 wyra\u017cenia "}
        <InlineMath math={exprLatex} />
        {" jest r\u00f3wna warto\u015bci wyra\u017cenia:"}
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>{"Twoja odpowied\u017a: "}</span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { a, b, c, d, s1, s2, exprLatex, coeffX2, coeffX, constant } = data;

  const firstExpanded = `${a * a}x^2${signedConstLatex(2 * a * s1 * b)}x+${b * b}`;
  const secondExpanded = `${c * c}x^2${signedConstLatex(2 * c * s2 * d)}x+${d * d}`;
  const afterRemovingParentheses = `${firstExpanded}-${c * c}x^2${signedConstLatex(-2 * c * s2 * d)}x-${d * d}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Rozwijamy oba kwadraty:
      </div>
      <BlockMath math={`(${a}x${signedConstLatex(s1 * b)})^2=${firstExpanded}`} />
      <BlockMath math={`(${c}x${signedConstLatex(s2 * d)})^2=${secondExpanded}`} />

      <div>
        <strong>Krok 2.</strong> {"Odejmujemy drugie wyra\u017cenie, opuszczamy nawias i redukujemy wyrazy podobne:"}
      </div>
      <BlockMath math={`${exprLatex}=(${firstExpanded})-(${secondExpanded})=`} />
      <BlockMath math={`=${afterRemovingParentheses}=`} />
      <BlockMath math={`=${polyLatex(coeffX2, coeffX, constant)}`} />
    </div>
  );
}
